import {
    useContext,
    createContext,
    useState,
    PropsWithChildren,
    useEffect,
} from 'react'

interface ConnectionContextProps {
    online: boolean
    saveFetch: (url: string, options: RequestInit) => Promise<Response>
}

interface Request {
    url: string
    options: RequestInit
}

const ConnectionContext = createContext<ConnectionContextProps>({
    online: false,
    saveFetch: fetch,
})

const ConnectionProvider = ({children}: PropsWithChildren) => {
    const [online, setOnline] = useState(false)

    const handleConnection = () => {
        if (navigator.onLine) {
            isReachable(getServerUrl()).then(function (online) {
                if (online) {
                    // handle online status
                    setOnline(true)
                } else {
                    setOnline(false)
                }
            })
        } else {
            // handle offline status
            setOnline(false)
        }
    }

    const isReachable = (url: string) => {
        return fetch(url, {method: 'HEAD', mode: 'no-cors'})
            .then(function (resp) {
                return resp && (resp.ok || resp.type === 'opaque')
            })
            .catch(function (err) {
                console.warn('[conn test failure]:', err)
            })
    }

    const getServerUrl = () => {
        return window.location.origin
    }

    const getFetchQueue = (): Request[] => {
        const currentItems = localStorage.getItem('fetch-queue') || '[]'
        return JSON.parse(currentItems)
    }

    const addFetchQueue = (request: Request) => {
        const currentItems = getFetchQueue()
        currentItems.push(request)
        localStorage.setItem('fetch-queue', JSON.stringify(currentItems))
    }

    const saveFetch = async (url: string, options: RequestInit) => {
        if (online) {
            return fetch(url, options)
        } else {
            addFetchQueue({url, options})
            console.log('currentItems', getFetchQueue())
            return new Response(null, {status: 500, statusText: 'Offline'})
        }
    }

    useEffect(() => {
        window.addEventListener('online', handleConnection)
        window.addEventListener('offline', handleConnection)
        handleConnection()

        return () => {
            window.removeEventListener('online', handleConnection)
            window.removeEventListener('offline', handleConnection)
        }
    }, [])

    return (
        <ConnectionContext.Provider value={{online, saveFetch}}>
            {children}
        </ConnectionContext.Provider>
    )
}

export default ConnectionProvider

export const useConnection = () => {
    return useContext(ConnectionContext)
}
